import { PAGE_NAME_TEMPLATE } from './constants';
import { replaceTemplate } from './replaceTemplate';

declare global {
    namespace Ya {
        namespace Rum {
            /**
             * Отложенная установка имени просматриваемой страницы.
             *
             * @kind helper
             * @param pageName — Имя просматриваемой страницы.
             * @returns undefined
             */
            function setDeferredPageName(pageName: string): void;

            /**
             * Сброс текущей просматриваемой страницы и всех данных, для которых она не была определена.
             *
             * @kind helper
             * @returns undefined
             */
            function resetDeferredPageName(): void;
        }
    }
}

function setupDeferredSend() {
    const rum = window.Ya.Rum;
    const originalSend = rum.send;

    let deferredPageName: string | null = null;
    let deferredSendArgs: Parameters<typeof originalSend>[] = [];

    /**
     * Переопрделенный Ya.Rum.send метод Rum счетчика, который позволяет не отправлять данные,
     * если в них не определена просматриваемая страница.
     *
     * @kind helper
     * @param args — Набор аргументов метода Ya.Rum.send, где args[2] является данными для отправки.
     * @returns undefined
     */
    rum.send = function send(...args: Parameters<typeof originalSend>) {
        // Если в данных для отправки нет шаблонного значения, значит эти данные можно сразу отправлять.
        // Например: события ошибок, которые также отправляются через Rum счетчик.
        if (args[2].indexOf(PAGE_NAME_TEMPLATE) === -1) {
            return originalSend(...args);
        }

        // Если просматриваемая страница уже определена, тогда сразу отправляем данные,
        // предварительно заменив в них шаблонное значение на актуальное.
        if (typeof deferredPageName === 'string') {
            args[2] = replaceTemplate(args[2], deferredPageName);

            return originalSend(...args);
        }

        // Если просматриваемая страница не определена, то накапливаем данные для отложенной отправки.
        deferredSendArgs.push(args);
    };

    /**
     * Отложенная установка имени просматриваемой страницы.
     *
     * @kind helper
     * @param pageName — Имя просматриваемой страницы.
     * @returns undefined
     */
    rum.setDeferredPageName = function setDeferredPageName(pageName: string) {
        deferredPageName = pageName;

        // Отправляем все накопленные данные в Rum,
        // предварительно заменив в них шаблонное значение на актуальное.
        for (const args of deferredSendArgs) {
            args[2] = replaceTemplate(args[2], deferredPageName);

            originalSend(...args);
        }

        deferredSendArgs = [];
    };

    /**
     * Сброс текущей просматриваемой страницы и всех данных, для которых она не была определена.
     *
     * @kind helper
     * @returns undefined
     */
    rum.resetDeferredPageName = function resetDeferredPageName() {
        deferredPageName = null;
        deferredSendArgs = [];
    };
}

/**
 * Инициализируем debug режим для метода Ya.Rum.send. Инициализация происходит асинхронно (из-за запроса в BlockStat),
 * поэтому подписываемся на событие 'debug:ready', после чего инициализируем deferred-send.
 */
if (process.env.RUM_DEBUG === '1') {
    window.Ya.Rum.subscribe('debug:ready', () => {
        setupDeferredSend();
    });

    require('@yandex-int/rum-counter/debug/blockstat');
} else {
    setupDeferredSend();
}
